import React, { useState, useEffect } from 'react';
import Button from '../../molecules/Button';
import Form from '../../molecules/Form/Form';
import Field from '../Field';
import { FiPlus } from 'react-icons/fi';
import { useForm } from '../../molecules/Form';
import { CreateListStyle } from './CreateListModal.style';
import linkedinService from '../../../services/linkedinService';
import Toast from '../../molecules/Toast';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import { areValuesEqual } from '../../../helpers/common';
import {
  companySizeOptions,
  contactedStatusOptions,
  dateCreatedOptions,
  fieldOperatorConfig,
  filterTypeConfig,
  leadSourceOptions,
  leadStatusOptions,
  languageOptions,
  optionsFilter,
  optionsOperatorInput,
  optionsOperatorSelect,
} from '../../Constant';

const CreateListModal = ({ onClose, onSuccess, isUpdateMode = false, existingListData = null }) => {
  const [form] = useForm();
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadCount, setLeadCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);

  const [listName, setListName] = useState("");
  const [filterRows, setFilterRows] = useState([
    { id: 1, filter: '', operator: '', value: '', visible: true, setActive: false, joinWithPrevious: null },
  ]);

  const { refetch } = useContextHook(AuthContext, v => ({
    refetch: v.refetch,
  }));

  useEffect(() => {
    fetchTags();
    fetchAllCampaigns();
    fetchAllLists();
  }, []);

  // Pre-fill filters when in update mode
  useEffect(() => {
    if (isUpdateMode && existingListData && campaigns.length > 0 && lists.length > 0) {
      // Set list name
      if (existingListData.name) {
        form.setFieldsValue({ text: existingListData.name });
        setListName(existingListData.name);
      }

      // Convert API filters to filterRows format
      if (existingListData.filters && existingListData.filters.conditions) {
        const conditions = existingListData.filters.conditions;

        const newFilterRows = conditions.map((condition, index) => {
          let normalizedOperator = normalizeOperatorFromPayload(condition.operator);
          const fieldKey = condition.field === 'email' ? 'email_exists' : condition.field;
          if (fieldKey === 'email_exists' || fieldKey === 'person_linkedin_url') {
            if (normalizedOperator === 'isNotEmpty') normalizedOperator = 'exist';
            if (normalizedOperator === 'isEmpty') normalizedOperator = 'notExist';
          }
          let displayValue = condition.value;

          // Fields that need ID-to-option conversion (store IDs, need option objects for display)
          const idBasedFields = ['campaigns', 'tags', 'list'];
          // Fields that are multi-select but store enum values as strings
          const enumMultiSelectFields = ['status', 'company_size'];
          // Single-select enum fields that need option object conversion
          const singleSelectFields = ['contacted_status', 'source', 'date_created'];
          const labelMultiSelectFields = ['language'];

          // If operator doesn't require a value, keep it empty (UI disables the value input)
          if (isOperatorWithoutValue(normalizedOperator)) {
            displayValue = '';
          } else if (idBasedFields.includes(condition.field)) {
            // Convert ID arrays to option objects for display
            const valueArray = Array.isArray(condition.value) ? condition.value : [condition.value];

            if (condition.field === 'campaigns') {
              displayValue = valueArray.map(id => campaigns.find(c => c.value === id)).filter(Boolean);
            } else if (condition.field === 'tags') {
              displayValue = valueArray.map(id => tags.find(t => t.value === id)).filter(Boolean);
            } else if (condition.field === 'list') {
              displayValue = valueArray.map(id => lists.find(l => l.value === id)).filter(Boolean);
            }
          } else if (enumMultiSelectFields.includes(condition.field)) {
            // Convert enum string arrays to option objects for display
            const valueArray = Array.isArray(condition.value) ? condition.value : [condition.value];

            const optionsMap = {
              'status': leadStatusOptions,
              'company_size': companySizeOptions,
            };
            const options = optionsMap[condition.field];
            displayValue = valueArray.map(val => options?.find(opt => opt.value === val)).filter(Boolean);
          } else if (labelMultiSelectFields.includes(condition.field)) {
            const valueArray = Array.isArray(condition.value) ? condition.value : [condition.value];
            displayValue = valueArray
              .map(val => languageOptions?.find(opt => opt.label === val || opt.value === val))
              .filter(Boolean);
          } else if (singleSelectFields.includes(condition.field)) {
            // Convert single-select enum fields to option objects for display
            const optionsMap = {
              'contacted_status': contactedStatusOptions,
              'source': leadSourceOptions,
              'date_created': dateCreatedOptions,
            };
            const options = optionsMap[condition.field];
            displayValue = options?.find(opt => opt.value === condition.value);
          }
          // For input fields (company_name, title, email), keep value as-is

          const row = {
            id: Date.now() + index,
            filter: fieldKey,
            operator: normalizedOperator,
            value: displayValue,
            visible: true,
            setActive: false,
            joinWithPrevious: condition.joinWithPrevious,
          };

          // Set form values for each row (operator expects option object)
          const operatorOptions = row.filter && fieldOperatorConfig[row.filter]
            ? fieldOperatorConfig[row.filter]
            : filterTypeConfig[row.filter] === 'select'
              ? optionsOperatorSelect
              : optionsOperatorInput;
          const operatorOption = operatorOptions?.find(opt => opt.value === normalizedOperator);
          const filterOption = optionsFilter.find(opt => opt.value === condition.field);

          form.setFieldsValue({
            [`filter_${row.id}`]: filterOption,
            [`operator_${row.id}`]: operatorOption || undefined,
            [`value_${row.id}`]: displayValue,
          });

          return row;
        });

        setFilterRows(newFilterRows);
        // Fetch lead count with pre-filled filters
        fetchLeadCount(newFilterRows);
      }
    }
  }, [isUpdateMode, existingListData, form, campaigns, lists, tags]);

  useEffect(() => {
    fetchLeadCount();
  }, [selectedTag]);

  const fetchTags = async () => {
    try {
      setLoadingTags(true);
      const res = await linkedinService.getTags();
      if (res?.success && res?.data) {
        const formattedTags = res.data.map(tag => ({
          label: tag?.name,
          value: tag?.id,
        }));
        setTags(formattedTags);
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to fetch tags' });
    } finally {
      setLoadingTags(false);
    }
  };

  const fetchAllCampaigns = async () => {
    try {
      const res = await linkedinService.getAllCampaigns();
      if (res?.success && res?.data) {
        const formattedCampaigns = res.data.map(campaign => ({
          label: campaign?.name,
          value: campaign?.id,
        }));
        setCampaigns(formattedCampaigns);
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to fetch campaigns' });
    }
  };

  const fetchAllLists = async () => {
    try {
      const res = await linkedinService.getAllLists();
      if (res?.success && res?.data) {
        const formattedLists = res.data.map(list => ({
          label: list?.name,
          value: list?.id,
        }));
        setLists(formattedLists);
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to fetch lists' });
    }
  };

  const rowHasValue = row => {
    const { value } = row || {};

    if (!value && value !== 0) return false;

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      return !!value.value;
    }

    return true;
  };

  const isSelectField = field => filterTypeConfig[field] === 'select';

  const isMultiSelectField = field =>
    field === 'status' ||
    field === 'tags' ||
    field === 'campaigns' ||
    field === 'company_size' ||
    field === 'list' ||
    field === 'language';

  const OPERATORS_WITHOUT_VALUE_UI = [
    'isEmpty',
    'isNotEmpty',
    'isEnlist',
    'isNotEnlist',
    'isTrue',
    'isFalse',
    'exist',
    'notExist',
  ];
  const OPERATORS_WITHOUT_VALUE_PAYLOAD = [
    'is_empty',
    'is_not_empty',
    'is_enlist',
    'is_not_enlist',
    'is_true',
    'is_false',
    'exist',
    'not_exist',
  ];

  const normalizeOperatorFromPayload = operator => {
    const operatorMap = {
      is_empty: 'isEmpty',
      is_not_empty: 'isNotEmpty',
      is_enlist: 'isEnlist',
      is_not_enlist: 'isNotEnlist',
      is_true: 'isTrue',
      is_false: 'isFalse',
      exist: 'exist',
      not_exist: 'notExist',
      not_contains: 'notContains',
    };
    return operatorMap?.[operator] || operator;
  };

  const isOperatorWithoutValue = operator =>
    OPERATORS_WITHOUT_VALUE_UI.includes(operator) || OPERATORS_WITHOUT_VALUE_PAYLOAD.includes(operator);

  // Check if value field should be disabled based on operator
  const shouldDisableValueField = operator => {
    return isOperatorWithoutValue(operator);
  };

  // Map internal operator values to payload format
  const mapOperatorToPayload = operator => {
    const operatorMap = {
      isEmpty: 'is_empty',
      isNotEmpty: 'is_not_empty',
      isEnlist: 'is_enlist',
      isNotEnlist: 'is_not_enlist',
      isTrue: 'is_true',
      isFalse: 'is_false',
      exist: 'is_not_empty',
      notExist: 'is_empty',
      notContains: 'not_contains',
    };
    return operatorMap[operator] || operator;
  };

  const normalizeSelectValue = (field, value) => {
    if (!isMultiSelectField(field)) {
      // Single-select: return single primitive value
      if (Array.isArray(value)) {
        const first = value[0];
        return first && typeof first === 'object' && first.value ? first.value : first;
      }
      if (typeof value === 'object' && value?.value) {
        return value.value;
      }
      return value;
    }

    // Language: store display names (e.g. "English"), not ISO codes
    if (field === 'language') {
      if (Array.isArray(value)) {
        return value
          .map(item => (typeof item === 'object' && item !== null ? item.label ?? item.value ?? item : item))
          .filter(v => v !== undefined && v !== null && v !== '');
      }
      if (typeof value === 'object' && value !== null) {
        const label = value.label ?? value.value ?? value;
        return label ? [label] : [];
      }
      return value !== undefined && value !== null && value !== '' ? [value] : [];
    }

    // Multi-select: always return array of primitive values
    if (Array.isArray(value)) {
      return value.map(item => item?.value || item).filter(v => v !== undefined && v !== null);
    }
    if (typeof value === 'object' && value?.value) {
      return [value.value];
    }
    return value !== undefined && value !== null ? [value] : [];
  };

  const buildConditionsFromRows = rows => {
    // Include rows that have a filter and either:
    // 1. Have a value (normal case), OR
    // 2. Have isEmpty/isNotEmpty/isEnlist/isNotEnlist operators (which don't need values)
    const validRows = rows.filter(row => {
      if (!row.filter || !row.operator) return false;

      // If operator doesn't require a value, include the row
      if (isOperatorWithoutValue(row.operator)) {
        return true; // Include these operators even without a value
      }

      return rowHasValue(row); // For other operators, require a value
    });

    return validRows.map((row, index) => {
      const field = row.filter;
      const isSelect = isSelectField(field);

      // For operators that don't need values, set value to null
      const needsValue = !isOperatorWithoutValue(row.operator);

      return {
        field,
        operator: mapOperatorToPayload(row.operator || (isSelect ? 'equals' : 'contains')),
        value: needsValue ? (isSelect ? normalizeSelectValue(field, row.value) : row.value) : null,
        joinWithPrevious: index === 0 ? null : row.joinWithPrevious || 'and',
      };
    });
  };

  const fetchLeadCount = async (rowsToUse = null) => {
    try {
      // Use provided rows or fall back to current filterRows state
      const rows = rowsToUse || filterRows;
      const conditions = buildConditionsFromRows(rows);

      if (conditions.length === 0) {
        setLeadCount(0);
        return;
      }

      setLoadingCount(true);

      const filters = { conditions };

      // Debug: inspect filters being sent to backend

      const res = await linkedinService.getLeadsCount({ filters });

      if (res?.success && res?.data && typeof res.data.leadCount === 'number') {
        setLeadCount(res.data.leadCount);
      }
    } catch (error) {
      console.error('Failed to fetch lead count:', error);
      setLeadCount(0);
    } finally {
      setLoadingCount(false);
    }
  };

  const handleClose = () => {
    const fieldNames = form.getFieldNames();
    const resetValues = {};
    fieldNames.forEach(name => {
      resetValues[name] = '';
    });
    form.setFieldsValue(resetValues);

    setFilterRows([{ id: 1, filter: '', operator: '', value: '', visible: true, joinWithPrevious: null }]);
    setSelectedTag(null);
    setLeadCount(0);
    setLoadingCount(false);
    setIsSubmitting(false);

    if (onClose) onClose();
  };

  const addFilterRow = () => {
    setFilterRows(prev => [
      ...prev,
      {
        id: Date.now(),
        filter: '',
        operator: '',
        value: '',
        visible: true,
        setActive: false,
        joinWithPrevious: 'and',
      },
    ]);
  };

  const updateFilterRow = (id, field, value) => {
    const updatedRows = filterRows.map(row => (row.id === id ? { ...row, [field]: value } : row));
    setFilterRows(updatedRows);

    const formValues = {};
    updatedRows.forEach(row => {
      formValues[`filter_${row.id}`] = row.filter;

      // For operator, find the full option object to set in form
      if (row.operator && row.filter) {
        const operatorOptions = row.filter && fieldOperatorConfig[row.filter]
          ? fieldOperatorConfig[row.filter]
          : filterTypeConfig[row.filter] === 'select'
            ? optionsOperatorSelect
            : optionsOperatorInput;
        const operatorOption = operatorOptions?.find(opt => opt.value === row.operator);
        formValues[`operator_${row.id}`] = operatorOption || undefined;
      } else {
        formValues[`operator_${row.id}`] = row.operator || undefined;
      }

      formValues[`value_${row.id}`] = row.value;
    });
    form.setFieldsValue(formValues);
  };

  const handleSubmit = async values => {
    try {
      setIsSubmitting(true);
      const conditions = buildConditionsFromRows(filterRows);

      const filters = {};

      if (conditions.length > 0) {
        filters.conditions = conditions;
      }

      const payload = { name: values.text, description: '', filters };

      let res;
      if (isUpdateMode && existingListData?.id) {

        res = await linkedinService.updateList({
          id: existingListData.id,
          ...payload,
        });
        Toast({ type: 'success', message: 'List updated successfully' });
      } else {
        res = await linkedinService.createList(payload);
        Toast({ type: 'success', message: res?.message || 'List created successfully' });
      }

      if (res?.success || res) {
        if (onSuccess) onSuccess();
        refetch();
        handleClose();
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || `Failed to ${isUpdateMode ? 'update' : 'create'} list` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteFilterRow = (id, index) => {
    if (index === 0) {
      // First, clear the form values to ensure they're reset
      // Use undefined to match what the controlled value prop expects
      form.setFieldsValue({
        [`filter_${id}`]: undefined,
        [`operator_${id}`]: undefined,
        [`value_${id}`]: '',
      });

      // Then update state to clear all fields using callback
      setFilterRows(prev => {
        const updatedRows = prev.map(row =>
          row.id === id
            ? {
              ...row,
              filter: '',
              operator: '',
              value: '',
            }
            : row,
        );

        // Trigger fetchLeadCount with cleared values
        fetchLeadCount(updatedRows);

        return updatedRows;
      });
      return;
    }
    // For other filters, remove the row and refresh lead count
    setFilterRows(prev => {
      const updatedRows = prev.filter(row => row.id !== id);
      // Trigger fetchLeadCount with updated rows after deletion
      fetchLeadCount(updatedRows);
      return updatedRows;
    });
  };

  return (
    <CreateListStyle>
      <Form form={form} onSubmit={handleSubmit}>
        <div className="content-holder">
          <div className="head">
            <strong className="title">{isUpdateMode ? 'Update List' : 'Create List'}</strong>
            <p>{isUpdateMode ? 'Update your list filters to refine your leads.' : 'Name your List and select how you want to filter your leads.'}</p>
          </div>
          <Form.Item
            colorBg
            inputSm
            label="List Name"
            type="text"
            rounded
            name="text"
            placeholder="Enter your list name"
            rules={[{ required: true, message: 'Name required' }]}
            value={listName}
            onChange={e => {
              setListName(e?.target?.value || "");
              form.setFieldsValue({ text: e?.target?.value || "" });
            }}>
            <Field />
          </Form.Item>
          <div className="filter-holder" style={{ position: 'relative', zIndex: 9 }}>
            <strong className="subtitle">Filters</strong>
            <div className="holder">
              {filterRows.map((row, index) => (
                <React.Fragment key={row.id}>
                  {row.visible && (
                    <>
                      {index > 0 && (
                        <div className="choose-col">
                          <div className="wrap">
                            {['And', 'Or', 'Not'].map(btn => (
                              <button
                                key={btn}
                                className={`btn-text ${row.joinWithPrevious === btn.toLowerCase() ? 'active' : ''}`}
                                onClick={() => {
                                  setFilterRows(prev => {
                                    const updatedRows = prev.map(r =>
                                      r.id === row.id ? { ...r, joinWithPrevious: btn.toLowerCase() } : r,
                                    );
                                    // Trigger fetchLeadCount with updated rows to avoid stale state
                                    fetchLeadCount(updatedRows);
                                    return updatedRows;
                                  });
                                }}
                                type="button">
                                {btn}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div
                        className={row.setActive ? 'select-row active' : 'select-row'}
                        onClick={() => {
                          setFilterRows(prev => prev.map(r => ({ ...r, setActive: r.id === row.id })));
                        }}>
                        <Form.Item
                          key={`filter-${row.id}-${row.filter || 'empty'}`}
                          colorBg
                          selectSm
                          type="select"
                          name={`filter_${row.id}`}
                          placeholder="Select Filter"
                          options={optionsFilter.filter(
                            opt => !filterRows.some(r => r.id !== row.id && r.filter === opt.value),
                          )}
                          value={row.filter ? optionsFilter.find(opt => opt.value === row.filter) : undefined}
                          onChange={e => {
                            const value = e?.target?.value?.value || e?.value || e;

                            // Don't trigger API if filter hasn't changed
                            if (row.filter === value) {
                              return;
                            }

                            // Clear form values FIRST before updating state
                            form.setFieldsValue({
                              [`filter_${row.id}`]: value ? optionsFilter.find(opt => opt.value === value) : undefined,
                              [`operator_${row.id}`]: undefined,
                              [`value_${row.id}`]: '',
                            });

                            // When changing filter (e.g. to "tags"), clear this row's operator and value
                            setFilterRows(prev => {
                              const updatedRows = prev.map(r =>
                                r.id === row.id
                                  ? {
                                    ...r,
                                    filter: value,
                                    operator: null,
                                    value: '',
                                  }
                                  : r,
                              );

                              // Trigger fetchLeadCount with updated rows to avoid stale state
                              if (value) {
                                fetchLeadCount(updatedRows);
                              } else {
                                setLeadCount(0);
                              }

                              return updatedRows;
                            });
                          }}>
                          <Field />
                        </Form.Item>
                        <Form.Item
                          key={`operator-${row.id}-${row.filter}`}
                          colorBg
                          selectSm
                          type="select"
                          name={`operator_${row.id}`}
                          placeholder="Select Operator"
                          options={
                            row.filter && fieldOperatorConfig[row.filter]
                              ? fieldOperatorConfig[row.filter]
                              : filterTypeConfig[row.filter] === 'select'
                                ? optionsOperatorSelect
                                : optionsOperatorInput
                          }
                          value={
                            row.operator
                              ? (row.filter && fieldOperatorConfig[row.filter]
                                ? fieldOperatorConfig[row.filter]
                                : filterTypeConfig[row.filter] === 'select'
                                  ? optionsOperatorSelect
                                  : optionsOperatorInput
                              )?.find(opt => opt.value === row.operator)
                              : undefined
                          }
                          onChange={e => {
                            // Extract the option object from the event
                            const selectedOption = e?.target?.value || e?.value || e;
                            // Get the value string from the option object
                            const operatorValue = selectedOption?.value ?? selectedOption;

                            if (!operatorValue) return;

                            // Don't trigger API if operator hasn't changed
                            if (row.operator === operatorValue) {
                              return;
                            }

                            // Clear value if switching to isEmpty, isNotEmpty, isEnlist, or isNotEnlist
                            const shouldClearValue = isOperatorWithoutValue(operatorValue);

                            // Update both operator and value in a single state update
                            setFilterRows(prev => {
                              const updatedRows = prev.map(r => {
                                if (r.id === row.id) {
                                  return {
                                    ...r,
                                    operator: operatorValue,
                                    value: shouldClearValue ? '' : r.value,
                                  };
                                }
                                return r;
                              });

                              // Trigger fetchLeadCount with updated rows to avoid stale state
                              if (operatorValue) {
                                fetchLeadCount(updatedRows);
                              }

                              return updatedRows;
                            });

                            // Update form values
                            const operatorOptions = row.filter && fieldOperatorConfig[row.filter]
                              ? fieldOperatorConfig[row.filter]
                              : filterTypeConfig[row.filter] === 'select'
                                ? optionsOperatorSelect
                                : optionsOperatorInput;
                            const operatorOption = operatorOptions?.find(opt => opt.value === operatorValue);

                            form.setFieldsValue({
                              [`operator_${row.id}`]: operatorOption || undefined,
                              [`value_${row.id}`]: shouldClearValue ? '' : undefined,
                            });
                          }}>
                          <Field />
                        </Form.Item>
                        <div className="input-wrap">
                          {filterTypeConfig[row.filter] === 'select' ? (
                            <Form.Item
                              key={`value-${row.id}-${row.filter}`}
                              colorBg
                              selectSm
                              type="select"
                              rounded
                              isMulti={
                                row.filter === 'status' ||
                                row.filter === 'tags' ||
                                row.filter === 'campaigns' ||
                                row.filter === 'company_size' ||
                                row.filter === 'list' ||
                                row.filter === 'language'
                              }
                              name={`value_${row.id}`}
                              placeholder={
                                row.filter === 'status'
                                  ? 'Select Lead Status'
                                  : row.filter === 'tags'
                                    ? 'Select Tags'
                                    : row.filter === 'campaigns'
                                      ? 'Select Campaigns'
                                      : row.filter === 'list'
                                        ? 'Select Lists'
                                        : row.filter === 'contacted_status'
                                          ? 'Select Contacted Status'
                                          : row.filter === 'source'
                                            ? 'Select Lead Source'
                                            : row.filter === 'date_created'
                                              ? 'Select Date Range'
                                              : row.filter === 'language'
                                                ? 'Select Languages'
                                                : row.filter === 'company_size'
                                                  ? 'Select Company Size'
                                                  : 'Select Value'
                              }
                              options={
                                row.filter === 'status'
                                  ? leadStatusOptions
                                  : row.filter === 'tags'
                                    ? tags
                                    : row.filter === 'campaigns'
                                      ? campaigns
                                      : row.filter === 'list'
                                        ? lists
                                        : row.filter === 'contacted_status'
                                          ? contactedStatusOptions
                                          : row.filter === 'source'
                                            ? leadSourceOptions
                                            : row.filter === 'date_created'
                                              ? dateCreatedOptions
                                              : row.filter === 'language'
                                                ? languageOptions
                                                : row.filter === 'company_size'
                                                  ? companySizeOptions
                                                  : []
                              }
                              value={row.value}
                              disabled={shouldDisableValueField(row.operator)}
                              onChange={e => {
                                const value = e?.target?.value || e;

                                // Don't trigger API if value hasn't changed
                                if (areValuesEqual(row.value, value)) {
                                  return;
                                }

                                // Calculate updated rows for immediate use
                                const updatedRows = filterRows.map(r => (r.id === row.id ? { ...r, value } : r));

                                // Update the row (this also updates form values)
                                updateFilterRow(row.id, 'value', value);

                                // Trigger fetchLeadCount with updated rows to avoid stale state
                                fetchLeadCount(updatedRows);
                              }}>
                              <Field />
                            </Form.Item>
                          ) : (
                            <Form.Item
                              key={`value-input-${row.id}-${row.filter}`}
                              colorBg
                              inputSm
                              name={`value_${row.id}`}
                              placeholder="Enter Value"
                              value={row.value}
                              disabled={shouldDisableValueField(row.operator)}
                              onChange={e => {
                                const value = e?.target?.value || '';

                                // Don't trigger API if value hasn't changed
                                if (areValuesEqual(row.value, value)) {
                                  return;
                                }

                                // Calculate updated rows for immediate use
                                const updatedRows = filterRows.map(r => (r.id === row.id ? { ...r, value } : r));

                                // Update the row (this also updates form values)
                                updateFilterRow(row.id, 'value', value);

                                // Trigger fetchLeadCount with updated rows to avoid stale state
                                fetchLeadCount(updatedRows);
                              }}>
                              <Field />
                            </Form.Item>
                          )}
                        </div>

                        <button className="btn-delete" type="button" onClick={() => deleteFilterRow(row.id, index)}>
                          <RiDeleteBin6Fill />
                        </button>
                      </div>
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>

            <button className="btn-add-filter" type="button" onClick={addFilterRow} newStyle variant="outline">
              <FiPlus size="18" /> Add Filter
            </button>
          </div>
        </div>
        <div className="btn-holder">
          <div className="total-leads">
            <strong>
              {loadingCount ? 'Loading...' : `${leadCount.toLocaleString()} Lead${leadCount !== 1 ? 's' : ''}`}
            </strong>
            &nbsp;match these filters
            {/* |{' '} */}
            {/* <a className="btn-preview" href="#">
              Preview Now
            </a> */}
          </div>
          <div className="btn-wrap">
            <Button
              newStyle
              rounded
              variant="underline"
              width="140px"
              onClick={handleClose}
              type="button"
              disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              newStyle
              rounded
              variant="darkBlue"
              width="155px"
              type="submit"
              disabled={isSubmitting || !listName.trim()}>
              {isSubmitting ? (isUpdateMode ? 'Updating...' : 'Creating...') : (isUpdateMode ? 'Update List' : 'Create List')}
            </Button>
          </div>
        </div>
      </Form>
    </CreateListStyle>
  );
};

export default CreateListModal;
